import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ExternalLink, Mic, Lock } from "lucide-react";
import { api, mediaUrl, thumbUrl } from "@/lib/api";
import { useMediaHold } from "@/lib/scroll";
import { useMember } from "@/context/MemberContext";

const PLATFORM_STYLE = {
  youtube: "bg-red-600/20 text-red-400 border-red-600/40",
  spotify: "bg-green-600/20 text-green-400 border-green-600/40",
  soundcloud: "bg-orange-600/20 text-orange-400 border-orange-600/40",
  video: "bg-royal/20 text-blue-400 border-royal/50",
  mp3: "bg-blood/20 text-blood border-blood/40",
};

function getEmbed(ep) {
  const url = ep.url || "";
  if (ep.platform === "youtube") {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (m) return { type: "iframe", src: `https://www.youtube.com/embed/${m[1]}`, aspect: true };
  }
  if (ep.platform === "spotify") {
    const m = url.match(/open\.spotify\.com\/(episode|show|track|playlist)\/(\w+)/);
    if (m) return { type: "iframe", src: `https://open.spotify.com/embed/${m[1]}/${m[2]}`, height: 232 };
  }
  if (ep.platform === "soundcloud") {
    return { type: "iframe", src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23e62435&inverse=true`, height: 166 };
  }
  const src = url.startsWith("http") ? url : mediaUrl(url);
  if (ep.platform === "video") return { type: "video", src };
  return { type: "audio", src };
}

function PlayerModal({ episode, onClose }) {
  useMediaHold(!!episode);
  const embed = episode ? getEmbed(episode) : null;
  return (
    <AnimatePresence>
      {episode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="episode-player-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="bg-elevate border border-white/15 w-full max-w-2xl p-6 sm:p-8 clip-frame"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-blood mb-2">Now Playing</p>
                <h3 className="font-display font-bold uppercase text-xl sm:text-2xl">{episode.title}</h3>
              </div>
              <button
                data-testid="episode-player-close-button"
                onClick={onClose}
                className="text-gray-400 hover:text-blood transition-colors"
                aria-label="Close player"
              >
                <X size={24} />
              </button>
            </div>

            {embed.type === "iframe" ? (
              <iframe
                data-testid="episode-player-embed"
                src={embed.src}
                title={episode.title}
                className="w-full border-0"
                style={{ height: embed.aspect ? undefined : embed.height || 232, aspectRatio: embed.aspect ? "16/9" : undefined }}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            ) : embed.type === "video" ? (
              <video
                data-testid="episode-player-video"
                src={embed.src}
                poster={episode.thumbnail ? thumbUrl(episode.thumbnail) : undefined}
                controls
                autoPlay
                className="w-full max-h-[60vh] bg-black"
              />
            ) : (
              <audio data-testid="episode-player-audio" src={embed.src} controls autoPlay className="w-full" />
            )}

            <a
              data-testid="episode-open-source-link"
              href={episode.url.startsWith("http") ? episode.url : mediaUrl(episode.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors"
            >
              Open on {episode.platform} <ExternalLink size={12} />
            </a>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Episodes() {
  const [episodes, setEpisodes] = useState([]);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(false);
  const { member, setPaywall } = useMember();

  useEffect(() => {
    api.get("/episodes").then((r) => setEpisodes(r.data)).catch(() => setError(true));
  }, [member]);

  const openEpisode = (ep) => (ep.locked ? setPaywall({ title: ep.title }) : setActive(ep));

  return (
    <section id="episodes" data-testid="episodes-section" className="relative py-20 lg:py-32 px-4 sm:px-8 lg:px-16 bg-surface/40">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-4">The Broadcast</p>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Latest Episodes
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-widest text-gray-500" data-testid="episodes-count">
            {episodes.length} transmission{episodes.length === 1 ? "" : "s"}
          </p>
        </motion.div>

        {error && (
          <p data-testid="episodes-error" className="font-mono text-sm text-blood">
            Signal lost — episodes could not be loaded.
          </p>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {episodes.map((ep, i) => (
            <motion.button
              key={ep.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              onClick={() => openEpisode(ep)}
              data-testid={`episode-card-${i}`}
              data-locked={ep.locked ? "true" : "false"}
              className="group text-left bg-elevate border border-white/10 hover:border-blood/60 p-6 sm:p-8 flex flex-col transition-colors duration-400 clip-frame"
            >
              {ep.thumbnail && (
                <div className="relative -mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-6 aspect-video overflow-hidden bg-black" data-testid={`episode-thumbnail-${i}`}>
                  <img
                    src={thumbUrl(ep.thumbnail)}
                    alt={ep.title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-elevate via-transparent to-transparent" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 flex items-center justify-center bg-blood/90 text-white group-hover:scale-110 transition-transform duration-300">
                      {ep.locked ? <Lock size={20} /> : <Play size={20} fill="currentColor" />}
                    </span>
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <span className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1 border ${PLATFORM_STYLE[ep.platform] || PLATFORM_STYLE.mp3}`}>
                  {ep.platform}
                </span>
                {ep.locked ? (
                  <span data-testid={`episode-lock-badge-${i}`} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-blood">
                    <Lock size={11} /> Insiders
                  </span>
                ) : ep.free ? (
                  <span data-testid={`episode-free-badge-${i}`} className="font-mono text-[10px] uppercase tracking-widest text-green-400">Free</span>
                ) : (
                  <span className="font-mono text-xs text-gray-500">{ep.duration || "—"}</span>
                )}
              </div>
              {!ep.thumbnail && (
                <span className="w-12 h-12 flex items-center justify-center bg-blood/15 text-blood group-hover:bg-blood group-hover:text-white transition-colors duration-300 mb-5">
                  <Play size={18} fill="currentColor" />
                </span>
              )}
              <h3 className="font-display font-bold uppercase text-xl sm:text-2xl leading-tight group-hover:text-blood transition-colors duration-300">
                {ep.title}
              </h3>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed line-clamp-3">{ep.description}</p>
            </motion.button>
          ))}
        </div>

        {!error && episodes.length === 0 && (
          <div data-testid="episodes-empty" className="flex items-center gap-3 text-gray-500 font-mono text-sm">
            <Mic size={16} /> First transmission drops soon.
          </div>
        )}
      </div>

      <PlayerModal episode={active} onClose={() => setActive(null)} />
    </section>
  );
}
